import React, { useState, useEffect } from 'react';
import { UsersIcon, EditIcon, TrashIcon, PlusIcon } from './Icons';
import { useNotification } from './Notification';

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState('user');
  const [visibleEmails, setVisibleEmails] = useState({});
  const { showNotification, showConfirm } = useNotification();
  
  // États pour les mappings de rôles Discord
  const [roleMappings, setRoleMappings] = useState([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [discordRoles, setDiscordRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [newMapping, setNewMapping] = useState({
    discord_role_id: '',
    discord_role_name: '',
    site_role: 'user'
  });

  const fetchUsers = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Erreur récupération utilisateurs:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(true);
    fetchRoleMappings();
  }, []);

  const fetchRoleMappings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/discord/role-mappings', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRoleMappings(data);
      }
    } catch (error) {
      console.error('Erreur récupération mappings:', error);
    }
  };

  const fetchDiscordRoles = async () => {
    setLoadingRoles(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/discord/guild-roles', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDiscordRoles(data);
      } else {
        console.error('Erreur lors de la récupération des rôles Discord');
      }
    } catch (error) {
      console.error('Erreur récupération rôles Discord:', error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleOpenRoleModal = (user) => {
    setEditingUser(user);
    setNewRole(user.role);
    setShowRoleModal(true);
  };

  const handleCloseRoleModal = () => {
    setShowRoleModal(false);
    setEditingUser(null);
    setNewRole('user');
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        showNotification('Rôle modifié avec succès', 'success');
        handleCloseRoleModal();
        fetchUsers(false);
      } else {
        const data = await response.json();
        showNotification(data.error || 'Erreur lors de la modification', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de la modification', 'error');
    }
  };

  const handleDelete = async (userId) => {
    const confirmed = await showConfirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?');
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showNotification('Utilisateur supprimé', 'success');
        fetchUsers(false);
      } else {
        const data = await response.json();
        showNotification(data.error || 'Erreur lors de la suppression', 'error');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      showNotification('Erreur lors de la suppression', 'error');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Jamais';
    return new Date(date).toLocaleString('fr-FR');
  };

  const toggleEmailVisibility = (userId) => {
    setVisibleEmails(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const handleAddMapping = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/discord/role-mappings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newMapping)
      });

      if (response.ok) {
        showNotification('Mapping de rôle ajouté avec succès', 'success');
        setShowMappingModal(false);
        setNewMapping({ discord_role_id: '', discord_role_name: '', site_role: 'user' });
        fetchRoleMappings();
      } else {
        const data = await response.json();
        showNotification(data.error || 'Erreur lors de l\'ajout', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de l\'ajout', 'error');
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    const confirmed = await showConfirm('Supprimer ce mapping de rôle ?');
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/discord/role-mappings/${mappingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showNotification('Mapping supprimé', 'success');
        fetchRoleMappings();
      } else {
        const data = await response.json();
        showNotification(data.error || 'Erreur lors de la suppression', 'error');
      }
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur lors de la suppression', 'error');
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>
          <UsersIcon size={28} />
          Gestion des Utilisateurs Discord
        </h2>
        <div style={{ color: '#9ca3af', fontSize: '14px' }}>
          {users.length} utilisateur(s) connecté(s)
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>Avatar</th>
              <th>Discord</th>
              <th>ID Discord</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Dernière connexion</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  {user.discord_avatar ? (
                    <img
                      src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.discord_avatar}.png`}
                      alt={user.discord_username}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: '2px solid #363759'
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#363759',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9ca3af',
                        fontSize: '12px'
                      }}
                    >
                      {user.discord_username ? user.discord_username[0].toUpperCase() : '?'}
                    </div>
                  )}
                </td>
                <td>
                  <strong>{user.discord_username || user.username || 'N/A'}</strong>
                  {user.discord_discriminator && user.discord_discriminator !== '0' && (
                    <span style={{ color: '#6b7280' }}>#{user.discord_discriminator}</span>
                  )}
                </td>
                <td style={{ color: '#9ca3af', fontSize: '12px', fontFamily: 'monospace' }}>
                  {user.discord_id || 'N/A'}
                </td>
                <td>
                  <span 
                    onClick={() => toggleEmailVisibility(user.id)}
                    style={{ 
                      color: '#9ca3af', 
                      fontSize: '13px',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      userSelect: 'none'
                    }}
                    title="Cliquez pour afficher/masquer"
                  >
                    {visibleEmails[user.id] ? (user.email || 'N/A') : '••••••@••••.•••'}
                  </span>
                </td>
                <td>
                  <span className={`role-badge ${user.role}`}>
                    {user.role}
                  </span>
                </td>
                <td style={{ color: '#9ca3af', fontSize: '13px' }}>
                  {formatDate(user.last_login)}
                </td>
                <td>
                  <button
                    className="btn btn-primary btn-small"
                    onClick={() => handleOpenRoleModal(user)}
                  >
                    <EditIcon size={14} />
                    Modifier rôle
                  </button>
                  <button
                    className="btn btn-danger btn-small"
                    onClick={() => handleDelete(user.id)}
                  >
                    <TrashIcon size={14} />
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showRoleModal && editingUser && (
        <div className="modal-overlay" onClick={handleCloseRoleModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Modifier le rôle de {editingUser.discord_username || editingUser.username}</h3>
            <form onSubmit={handleRoleSubmit}>
              <div className="form-group">
                <label>Rôle</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#252641',
                    border: '1px solid #363759',
                    borderRadius: '8px',
                    color: '#e5e7eb',
                    fontSize: '14px'
                  }}
                >
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCloseRoleModal}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  Modifier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Section Mappings de Rôles Discord */}
      <div style={{ marginTop: '40px' }}>
        <div className="admin-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <UsersIcon size={28} />
            Mappings Rôles Discord
          </h2>
          <button className="btn btn-primary" onClick={() => setShowMappingModal(true)}>
            <PlusIcon size={18} />
            Ajouter un mapping
          </button>
        </div>

        {roleMappings.length === 0 ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#9ca3af', background: '#252641', borderRadius: '8px', border: '1px solid #363759' }}>
            <p>Aucun mapping de rôle configuré</p>
            <p style={{ fontSize: '13px', marginTop: '10px' }}>Ajoutez un mapping pour attribuer automatiquement les rôles du site en fonction des rôles Discord</p>
          </div>
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>ID Rôle Discord</th>
                <th>Nom du Rôle Discord</th>
                <th>Rôle du Site</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roleMappings.map((mapping) => (
                <tr key={mapping.id}>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#9ca3af' }}>
                    {mapping.discord_role_id}
                  </td>
                  <td>
                    <strong>{mapping.discord_role_name}</strong>
                  </td>
                  <td>
                    <span className={`role-badge ${mapping.site_role}`}>
                      {mapping.site_role}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-danger btn-small"
                      onClick={() => handleDeleteMapping(mapping.id)}
                    >
                      <TrashIcon size={14} />
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal pour ajouter un mapping */}
      {showMappingModal && (
        <div className="modal-overlay" onClick={() => setShowMappingModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Ajouter un Mapping de Rôle Discord</h3>
            <form onSubmit={handleAddMapping}>
              <div className="form-group">
                <label>Rôle Discord</label>
                {loadingRoles ? (
                  <div style={{ padding: '12px', color: '#9ca3af' }}>
                    Chargement des rôles...
                  </div>
                ) : discordRoles.length === 0 ? (
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={fetchDiscordRoles}
                      style={{ width: '100%', marginBottom: '10px' }}
                    >
                      Charger les rôles Discord
                    </button>
                  </div>
                ) : (
                  <select
                    value={newMapping.discord_role_id}
                    onChange={(e) => {
                      const selectedRole = discordRoles.find(r => r.id === e.target.value);
                      setNewMapping({
                        ...newMapping,
                        discord_role_id: e.target.value,
                        discord_role_name: selectedRole ? selectedRole.name : ''
                      });
                    }}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#252641',
                      border: '1px solid #363759',
                      borderRadius: '8px',
                      color: '#e5e7eb',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Sélectionnez un rôle Discord</option>
                    {discordRoles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                )}
                <small style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                  Sélectionnez le rôle Discord à mapper avec un rôle du site
                </small>
              </div>
              <div className="form-group">
                <label>Rôle du Site</label>
                <select
                  value={newMapping.site_role}
                  onChange={(e) => setNewMapping({ ...newMapping, site_role: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#252641',
                    border: '1px solid #363759',
                    borderRadius: '8px',
                    color: '#e5e7eb',
                    fontSize: '14px'
                  }}
                >
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowMappingModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={!newMapping.discord_role_id}>
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
